/**
 * Auto-heal sessioni E2E: non scartare ciphertext, richiedi/ri-scambia chiavi, ritenta decrypt.
 */
import {
  buildMsgKeyOffer,
  buildMsgResyncRequest,
} from './zionControl.ts'
import {
  bumpPendingAttempt,
  fingerprintBlob,
  gcPendingDecrypt,
  listAllPendingDecrypt,
  listPendingDecryptForContact,
  removePendingDecrypt,
  savePendingDecrypt,
} from './pendingDecryptStore.ts'
import { PENDING_DECRYPT_PLACEHOLDER } from '../crypto/ZionCipher.ts'
import { tryDecryptFromAllContacts } from '../crypto/zionPrivacyCrypto.ts'
import {
  fetchContactPublicKey,
  updateContactPublicKey,
} from '../contacts/contactKeyService.ts'
import { getPrivateKey, getPublicKeyBase64 } from '../storage/keyStore.ts'
import {
  appendMessage,
  deleteMessage,
  messageExists,
  patchMessage,
} from '../storage/chatStore.ts'
import {
  findContact,
  findContactBySession,
  listContacts,
  saveContactAtomic,
  type ZionContact,
} from '../storage/pairingStore.ts'
import { getSessionToken } from '../storage/sessionStore.ts'

type SendControlFn = (contactId: string, plain: string) => Promise<boolean>
type ReprocessBlobFn = (
  blob: string,
  opts?: { hintPeerSession?: string; hintMessageId?: string },
) => Promise<boolean>

const resyncCooldown = new Map<string, number>()
const RESYNC_COOLDOWN_MS = 8_000

let sendControlFn: SendControlFn | null = null
let reprocessBlobFn: ReprocessBlobFn | null = null

export function bindSessionHealHandlers(opts: {
  sendControl: SendControlFn
  reprocessBlob: ReprocessBlobFn
}): void {
  sendControlFn = opts.sendControl
  reprocessBlobFn = opts.reprocessBlob
  void gcPendingDecrypt()
}

function canResync(contactId: string): boolean {
  const last = resyncCooldown.get(contactId) || 0
  if (Date.now() - last < RESYNC_COOLDOWN_MS) return false
  resyncCooldown.set(contactId, Date.now())
  return true
}

async function resolveContactHint(
  peerSession?: string,
): Promise<ZionContact | null> {
  const sess = peerSession?.trim()
  if (!sess) return null
  return (await findContactBySession(sess)) ?? null
}

/**
 * Decrypt fallito: salva ciphertext, placeholder in chat, refresh chiave + resync request.
 * MAI scartare in silenzio.
 */
export async function onDecryptFailure(
  blob: string,
  opts: { hintPeerSession?: string } = {},
): Promise<void> {
  const contact = await resolveContactHint(opts.hintPeerSession)
  const id = await fingerprintBlob(blob)
  const pending = await savePendingDecrypt({
    id,
    blob,
    contactId: contact?.contactId,
    peerSession: opts.hintPeerSession?.trim() || contact?.sessionToken,
  })

  if (contact) {
    const placeholderId = pending.placeholderMsgId || `pd-${id}`
    if (!(await messageExists(placeholderId))) {
      await appendMessage({
        id: placeholderId,
        contactId: contact.contactId,
        text: PENDING_DECRYPT_PLACEHOLDER,
        outgoing: false,
        timestamp: Date.now(),
        pending: true,
      })
      await savePendingDecrypt({
        ...pending,
        placeholderMsgId: placeholderId,
      })
    }

    // 1) Chiave locale già presente — NON chiamare /publickey (ZK → 410/404)
    //    Refresh server solo se manca del tutto (fetchContactPublicKey già gestisce 404/410)
    if (!contact.publicKey.trim()) {
      try {
        const fresh = await fetchContactPublicKey(contact.nickname)
        if (fresh) {
          await updateContactPublicKey(contact, fresh)
          console.log('[ZION heal] got peer key from server', contact.contactId.slice(0, 8))
        }
      } catch (e) {
        console.warn('[ZION heal] server key fetch failed', e)
      }
    }

    // 2) Retry immediato dopo refresh
    const healed = await retryPendingForContact(contact.contactId)
    if (healed > 0) return

    // 3) Chiedi al peer di offrire la chiave (control cifrato)
    if (canResync(contact.contactId) && sendControlFn) {
      const sess = (await getSessionToken())?.trim() || ''
      void sendControlFn(contact.contactId, buildMsgResyncRequest(sess))
    }
  }
}

/** Peer ha mandato MSG_RESYNC_REQUEST → rispondi con KEY_OFFER (chiave pubblica nostra). */
export async function respondToResyncRequest(contact: ZionContact): Promise<void> {
  const myPub = (await getPublicKeyBase64()).trim()
  if (!myPub || !sendControlFn) return
  // Usa chiave locale del peer; niente fetch /publickey (ZK → 410)
  const live = (await findContact(contact.contactId)) ?? contact
  await sendControlFn(live.contactId, buildMsgKeyOffer(myPub))
  console.log('[ZION heal] sent KEY_OFFER to', live.contactId.slice(0, 8))
}

/** Peer ha mandato KEY_OFFER / NYXIA_KEYX → aggiorna chiave atomica e ritenta pending. */
export async function applyPeerKeyOffer(contact: ZionContact, publicKey: string): Promise<void> {
  const key = publicKey.trim()
  if (!key) return
  await saveContactAtomic(contact.contactId, async (current) => {
    if (!current) return null
    return { ...current, publicKey: key }
  })
  console.log('[ZION heal] applied KEY_OFFER', contact.contactId.slice(0, 8))
  await retryPendingForContact(contact.contactId)
  // Ritenta anche pending senza contactId ma con stesso peerSession
  const sess = contact.sessionToken.trim()
  if (sess) {
    const all = await listAllPendingDecrypt()
    for (const row of all) {
      if (row.contactId) continue
      if (row.peerSession?.trim().toLowerCase() === sess.toLowerCase()) {
        await savePendingDecrypt({ ...row, contactId: contact.contactId })
      }
    }
    await retryPendingForContact(contact.contactId)
  }
}

export async function retryPendingForContact(contactId: string): Promise<number> {
  const rows = await listPendingDecryptForContact(contactId)
  if (!rows.length) return 0
  let ok = 0
  for (const row of rows) {
    await bumpPendingAttempt(row.id)
    const success = reprocessBlobFn
      ? await reprocessBlobFn(row.blob, {
          hintPeerSession: row.peerSession,
          hintMessageId: row.placeholderMsgId,
        })
      : await tryDecryptPendingLocally(row.blob, contactId)
    if (success) {
      ok++
      if (row.placeholderMsgId) {
        try {
          await deleteMessage(row.placeholderMsgId)
        } catch {
          /* ignore */
        }
      }
      await removePendingDecrypt(row.id)
    }
  }
  return ok
}

async function tryDecryptPendingLocally(blob: string, contactId: string): Promise<boolean> {
  const privateKey = await getPrivateKey()
  const contacts = await listContacts()
  const match = await tryDecryptFromAllContacts(privateKey, blob, contacts)
  if (!match) return false
  const contact = contacts[match.index]
  if (!contact || contact.contactId !== contactId) return false
  // Solo verifica decrypt — il reprocess completo lo fa reprocessBlobFn
  return true
}

/** Dopo bootstrap: ritenta tutti i pending. */
export async function retryAllPendingDecrypts(): Promise<void> {
  const contacts = await listContacts()
  for (const c of contacts) {
    await retryPendingForContact(c.contactId)
  }
  // Pending orfani: prova decrypt generico
  if (!reprocessBlobFn) return
  const orphans = (await listAllPendingDecrypt()).filter((r) => !r.contactId)
  for (const row of orphans) {
    await bumpPendingAttempt(row.id)
    const ok = await reprocessBlobFn(row.blob, { hintPeerSession: row.peerSession })
    if (ok) await removePendingDecrypt(row.id)
  }
}

export async function markPlaceholderResolved(placeholderMsgId: string, finalText: string): Promise<void> {
  if (!placeholderMsgId) return
  if (!(await messageExists(placeholderMsgId))) return
  await patchMessage(placeholderMsgId, {
    text: finalText,
    pending: false,
  })
}
